// src/shared/dashboard/shell/MessageInstructorWidget.tsx
//
// Standalone "Message {instructor}" widget — the contact entry point for the
// human instructor behind the AI coach (e.g. "Message Scott"). Reads the active
// vertical's `dashboard.instructorContact`, or a course-workspace override when
// that course owns the instructor relationship. Two variants so it can live in
// both shells:
//   - "rail" : a flat paper card in the right rail, ABOVE the AI mentor card (2xl+).
//   - "fab"  : a labelled floating pill above the bottom nav (below 2xl: phone +
//              tablet, where the right rail is hidden).
// Both open the same MessageInstructorModal.
import { useState } from "react";
import { Mail } from "lucide-react";
import { useT } from "@/shared/i18n/useT";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { MessageInstructorModal } from "@/shared/components/MessageInstructorModal";

type InstructorContact = {
  name: string;
  fullName?: string;
  title?: string;
  photoUrl?: string;
};

export function MessageInstructorWidget({
  variant,
  contact: contactOverride,
}: {
  variant: "rail" | "fab";
  contact?: InstructorContact;
}) {
  const t = useT();
  const { verticalConfig } = useVertical();
  const [open, setOpen] = useState(false);

  const contact = contactOverride ?? verticalConfig.dashboard.instructorContact;
  if (!contact) return null;
  const name = contact.name;
  const fullName = contact.fullName ?? contact.name;
  const initials = fullName
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      {variant === "rail" ? (
        <div className="shadow-[var(--shadow-surface)] w-full border border-[var(--hairline)] bg-white p-5">
          <p className="ed-mono text-[10px] text-[var(--muted)] mb-4">
            {t("shell.instructor")}
          </p>

          {/* Instructor identity — photo (or initials) + name + role. */}
          <div className="flex items-center gap-3">
            {contact.photoUrl ? (
              <img
                src={contact.photoUrl}
                alt={fullName}
                className="h-12 w-12 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="h-12 w-12 shrink-0 rounded-full bg-[var(--ink)] grid place-items-center text-[var(--paper)] ed-mono text-[13px]">
                {initials || "IN"}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="ed-serif text-[17px] text-[var(--ink-2)] truncate">
                {fullName}
              </h3>
              {contact.title && (
                <p className="text-[12.5px] text-[var(--muted)] truncate">
                  {contact.title}
                </p>
              )}
            </div>
          </div>

          <p className="text-[13px] text-[var(--muted)] leading-relaxed mt-4 mb-4">
            {t("shell.messageInstructorBlurb", { name })}
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--ink)] px-3 py-2.5 text-[13px] font-medium text-white transition-all hover:opacity-90 active:scale-[0.98] cursor-pointer"
          >
            <Mail size={14} />
            {t("shell.messageInstructor", { name })}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("shell.messageInstructor", { name })}
          className="fixed right-4 z-40 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] inline-flex min-h-11 items-center gap-2 rounded-full bg-[var(--ink)] py-2 pl-2 pr-5 text-[13px] font-medium text-white shadow-[var(--shadow-menu)] transition-colors hover:bg-[var(--action-bg-hover)] cursor-pointer lg:bottom-6 2xl:hidden"
        >
          {contact.photoUrl ? (
            <img
              src={contact.photoUrl}
              alt=""
              className="h-7 w-7 rounded-full object-cover"
            />
          ) : (
            <span className="grid h-7 w-7 place-items-center rounded-full bg-white/15">
              <Mail size={15} />
            </span>
          )}
          {t("shell.messageInstructor", { name })}
        </button>
      )}

      {open && (
        <MessageInstructorModal
          instructorName={name}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
